import { InputHTMLAttributes } from 'react'
import { useController } from 'react-hook-form'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

/** Props of the underlying element; the value binding stays with the form. */
export type TControlledProps<P> = Omit<P, 'value' | 'onChange' | 'onBlur' | 'name'>

export type TInputField<T> = TCommonFields<T> & {
    /** `'textarea'` is kept for existing field definitions, new ones should use `TTextareaField` */
    type: 'text' | 'textarea' | 'number'
    labelTransition?: boolean
    default?: number | string
    loadDefault?: () => Promise<number | string>
    props?: TControlledProps<InputHTMLAttributes<HTMLInputElement>>
    append?: React.ReactNode
}

type TProps<T> = {
    rowField: TInputField<T>
    fieldName?: string
    labelWidth?: string
}

const InputField = <T,>({ rowField, fieldName, labelWidth }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)
    const fieldName_ = fieldName ?? rowField.name

    const {
        field: { onChange, value, disabled: _disabled, ...fieldProps },
    } = useController({
        name: fieldName_,
        rules: getFieldRules(rowField, required),
    })

    const inputComponent = (
        <input
            id={fieldName_}
            {...fieldProps}
            {...rowField.props}
            type={rowField.type}
            // `?? ''` keeps the input controlled, so a form reset clears the text in the DOM too
            value={value ?? ''}
            onChange={e => {
                const value: string | number =
                    e.target.value === '' || rowField.type !== 'number'
                        ? e.target.value
                        : +e.target.value

                onChange(value)
                rowField.onChange?.(value)
            }}
            required={required}
            disabled={disabled}
        />
    )

    return (
        <FieldRow
            fieldName={fieldName_}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-input"
            labelTransition={rowField.labelTransition}
            filled={rowField.labelTransition && !['', undefined, null].includes(value)}
        >
            {rowField.append ? (
                <div className="form-input-appended">
                    {inputComponent}
                    {rowField.append}
                </div>
            ) : (
                inputComponent
            )}
        </FieldRow>
    )
}

export default InputField
